import { metrics, type Counter, type Histogram } from '@opentelemetry/api';

/**
 * Instrument registry.
 *
 * The Logfire JS SDK ships no metric helpers (unlike Python), so this is a thin
 * typed layer over `@opentelemetry/api` whose real job is to declare every
 * name, unit and description exactly once.
 *
 * COST: Logfire bills 1 metric point = 1 span = 1 unit. A metric point is
 * emitted per (series × export interval), so N attribute combinations at a 60s
 * interval is N × 43,200 units/month. Keep dimensions closed and small.
 *
 * `org_id` is deliberately NOT a metric dimension — org count is unbounded and
 * growing. Per-org usage comes from SQL over spans, which already carry
 * `firmable.org_id` via baggage.
 *
 * Logfire stores Gauge / Sum / Histogram / ExponentialHistogram. It does NOT
 * store Summary — never use one.
 */
export const METER_NAME = 'firmable';

export const Instrument = {
  CREDITS_CONSUMED: { name: 'firmable.credits.consumed', unit: '{credit}', description: 'Credits charged to customers' },
  USAGE_PROFILES: { name: 'firmable.usage.profiles', unit: '{profile}', description: 'Profiles recorded against customer usage' },
  SEARCH_REQUESTS: { name: 'firmable.search.requests', unit: '{request}', description: 'Outbound Search API calls' },
  SEARCH_DURATION: { name: 'firmable.search.duration', unit: 'ms', description: 'Search API round-trip duration' },
  CRM_PUSHES: { name: 'firmable.crm.pushes', unit: '{push}', description: 'Records pushed to a CRM' },
  PARTNER_USAGE: { name: 'firmable.partner.usage', unit: '{call}', description: 'Partner API calls, by partner and outcome' },
} as const;

export type InstrumentSpec = (typeof Instrument)[keyof typeof Instrument];

/**
 * The only attributes permitted on a metric. Adding one is a deliberate,
 * type-checked act because every new combination multiplies the series count.
 */
export interface MetricDimensions {
  'firmable.client_source'?: string;
  'firmable.request_origin'?: string;
  'firmable.feature'?: string;
  'firmable.usage.kind'?: string;
  'firmable.category'?: string;
  'firmable.crm.provider'?: string;
  'firmable.partner'?: string;
  /** One of 9 usage outcomes; with a handful of partners the series count stays small. */
  'firmable.partner.outcome'?: string;
  'firmable.credits.source'?: 'subscription' | 'addon';
  'http.response.status_code'?: number;
  'error.type'?: string;
}

const counters = new Map<string, Counter>();
const histograms = new Map<string, Histogram>();

export function counter(spec: InstrumentSpec): Counter {
  let existing = counters.get(spec.name);
  if (!existing) {
    existing = metrics.getMeter(METER_NAME).createCounter(spec.name, { unit: spec.unit, description: spec.description });
    counters.set(spec.name, existing);
  }
  return existing;
}

export function histogram(spec: InstrumentSpec): Histogram {
  let existing = histograms.get(spec.name);
  if (!existing) {
    existing = metrics.getMeter(METER_NAME).createHistogram(spec.name, { unit: spec.unit, description: spec.description });
    histograms.set(spec.name, existing);
  }
  return existing;
}

/** Safe to call before a MeterProvider exists — the API no-ops rather than throwing. */
export function addCount(spec: InstrumentSpec, value: number, dimensions: MetricDimensions = {}): void {
  try {
    counter(spec).add(value, dimensions as Record<string, string | number>);
  } catch {
    // Never let a metric break a request path.
  }
}

export function record(spec: InstrumentSpec, value: number, dimensions: MetricDimensions = {}): void {
  try {
    histogram(spec).record(value, dimensions as Record<string, string | number>);
  } catch {
    // Never let a metric break a request path.
  }
}
